/* tslint:disable:prefer-for-of */
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { HttpService } from '../http';
import {
    ApiEndpoint,
    ErrorResponse,
    EticketListResponse,
    PriceListResponse,
    PromotionCodeCheckRequest,
    PromotionCodeCheckResponse,
} from '../../webshop-api';
import {
    ApiEndpoint as OnlineTicketsEndpoint,
    SharedTicketListResponse,
    SharedToken,
    ShareTicketRequest
} from '../../webshop-api-online-tickets';
import { ETicketUtil } from '../e-ticket/e-ticket-util.service';
import { SessionStore } from '../storage/storage.interface';
import { SessionStoreKey } from '../storage/session-store-key.enum';
import { ObjectUtils } from '../object';
import { StorageService } from '../storage';
import { LanguageCode } from '../translation';
import { ApiConfiguration } from './api.configuration';
import { AuthTokenRequest, PriceListRequest } from './api.interfaces';

export interface WindowWithApi extends Window {
    DWSApiServiceInstance: ApiService;
}

export class ApiService {

    public static readonly identifier = '$apiService';

    /**
     * Get ApiService instance
     */
    public static getInstance(configuration: ApiConfiguration): ApiService {
        const windowInstance = this.getDOMInstance();
        if (windowInstance && windowInstance.configuration.clientId === configuration.clientId) {
            return windowInstance;
        }

        if (!this.instance) {
            this.instance = new ApiService(configuration);
            (window as WindowWithApi & typeof globalThis).DWSApiServiceInstance = this.instance;
        }

        return this.instance;
    }

    /**
     * Get configuration from DOM
     */
    public static getInjectedConfiguration(documentRef: Document, scriptName?: string): ApiConfiguration {
        const scriptElementList = documentRef.querySelectorAll('script');
        let scriptElement: HTMLScriptElement | undefined;

        if (!scriptElementList) {
            throw new Error('No script elements found');
        }

        if (!scriptName) {
            return {
                clientId: parseInt(
                    scriptElementList.item(scriptElementList.length - 1).getAttribute('data-pool-id') || '', 10,
                ),
            };
        }

        for (let i = 0; i < scriptElementList.length; i++) {
            const element = scriptElementList[i];

            if (element.hasAttribute('src') &&
                element.hasAttribute('data-pool-id') &&
                element.getAttribute('src')!.indexOf(scriptName) > -1) {
                scriptElement = element;
            }
        }

        if (scriptElement) {
            return {
                clientId: parseInt(scriptElement.getAttribute('data-pool-id') || '', 10),
            };
        }

        throw new Error(`No element with ${scriptName} in src and data-pool-id attribute`);
    }

    private static instance: ApiService;

    /**
     * Get instance attached to DOM window if available
     */
    private static getDOMInstance(): ApiService | undefined {
        const win: WindowWithApi = window as WindowWithApi & typeof globalThis;

        if (!win) {
            return undefined;
        }

        if (win.DWSApiServiceInstance === undefined) {
            return undefined;
        }

        return win.DWSApiServiceInstance;
    }

    public readonly configuration: ApiConfiguration;

    private http: HttpService;

    /**
     * Api service ctor
     */
    constructor(configuration: ApiConfiguration) {
        this.http = HttpService.getInstance(configuration);
        this.configuration = configuration;
    }

    /**
     * Get eticket list for authenticated user
     */
    public async getEticketList(getCachedIfAvailable: boolean = false): Promise<EticketListResponse | ErrorResponse> {
        const storageService = StorageService.getInstance({poolId: this.configuration.clientId});
        let session: SessionStore = storageService.loadSession();
        const TTL = 1000 * 60 * 30;

        // Check if eticket list is available in session and return session value
        if (getCachedIfAvailable &&
            ETicketUtil.isEticketListInSession(session) &&
            ETicketUtil.isEticketListValidForSession(session as SessionStore)
        ) {
            const existingSession = session as SessionStore;
            let response: EticketListResponse | ErrorResponse | undefined = existingSession[SessionStoreKey.EticketList];

            if (response !== undefined && response.hasOwnProperty('code') && response.hasOwnProperty('message')) {
                return response as ErrorResponse;
            }

            if (response !== undefined) {
                response = existingSession[SessionStoreKey.EticketList] as EticketListResponse;

                if (response.customerList && response.customerList.length === undefined) {
                    response.customerList = ObjectUtils.parseObjectValuesToArray(response.customerList);
                }

                if (response.eticketList && response.eticketList.length === undefined) {
                    response.eticketList = ObjectUtils.parseObjectValuesToArray(response.eticketList);
                }

                if (response.eventList && response.eventList.length === undefined) {
                    response.eventList = ObjectUtils.parseObjectValuesToArray(response.eventList);
                }

                return response;
            }
        }

        try {
            const promiseReturn = await this.http.get<EticketListResponse>(
                this.getCompiledEndpointFor(ApiEndpoint.getEticketList),
            );
            session = storageService.loadSession();
            const store: SessionStore = ObjectUtils.deepClone(session) as SessionStore;

            store[SessionStoreKey.EticketList] = promiseReturn.data;
            store[SessionStoreKey.EticketListTTL] = Date.now() + TTL;
            storageService.saveSession(store);

            return promiseReturn.data;
        } catch (e) {
            const error = (<AxiosResponse> e).data as ErrorResponse;
            const store: SessionStore = ObjectUtils.deepClone(session) as SessionStore;

            store[SessionStoreKey.EticketList] = error;
            store[SessionStoreKey.EticketListTTL] = undefined;
            storageService.saveSession(store);

            return error;
        }
    }

    /**
     * TDL promotion code validation request
     */
    public async validatePromotionCode(requestParams: PromotionCodeCheckRequest): Promise<PromotionCodeCheckResponse> {
        return this.postResource<PromotionCodeCheckResponse>(
            this.getCompiledEndpointFor(ApiEndpoint.promotionCodeCheck),
            requestParams
        );
    }

    /**
     * PriceList API request
     */
    public async getPriceList(params: PriceListRequest): Promise<PriceListResponse | ErrorResponse> {
        let response: PriceListResponse | ErrorResponse;

        try {
            response = (
                await this.http.get<PriceListResponse>(this.getCompiledEndpointFor(ApiEndpoint.priceList), {params})
            ).data;
        } catch (e) {
            response = (<AxiosResponse> e).data as ErrorResponse;
        }

        return response;
    }

    /**
     * whatItDoes
     * retrieves list of shared tickets for provided token
     */
    public async getSharedTickets(params: SharedToken): Promise<SharedTicketListResponse|ErrorResponse> {
        let response: SharedTicketListResponse | ErrorResponse;

        try {
            response = (
                await this.http.get<SharedTicketListResponse>(OnlineTicketsEndpoint.apiSharedTicketsGet, { params })
            ).data;
        } catch (e) {
            response = (<AxiosResponse> e).data as ErrorResponse;
        }

        return response;
    }

    /**
     * whatItDoes
     * shares your ticket with email recipient
     */
    public async shareOnlineTicket(body: ShareTicketRequest): Promise<string| ErrorResponse> {
        return this.postResource<string|ErrorResponse>(
            OnlineTicketsEndpoint.createOnlineTicketShare,
            body
        );
    }

    /**
     * whatItDoes
     * revokes shared online ticket
     */
    public async revokeSharedOnlineTicket(id: number, language: LanguageCode = LanguageCode.English): Promise<any> {
        return this.http.delete<any>(
            `${OnlineTicketsEndpoint.createOnlineTicketShare}/${id}?lang=${language}`
        );
    }

    /**
     * Common API post wrapper method
     */
    private async postResource<T>(
        endpoint: ApiEndpoint|OnlineTicketsEndpoint,
        data: any,
        config: AxiosRequestConfig = {}
    ): Promise<T> {
        try {
            const requestPromise = await this.http.post<T>(endpoint, data, config);

            return Promise.resolve(requestPromise.data);
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Endpoint string retrieval helper method
     */
    private getCompiledEndpointFor(endpoint: ApiEndpoint): ApiEndpoint {
        return `${endpoint}` as ApiEndpoint;
    }

    /**
     * Search
     * @param searchParams
     */
    public async search(searchParams: any): Promise<any> {
        return this.http.get<any>(
            this.getCompiledEndpointFor(ApiEndpoint.search),
            {params: searchParams}
        );
    }

    /**
     * Search Artists
     * @param searchParams
     */
    public async searchArtists(searchParams: any): Promise<any> {
        return this.http.get<any>(
                this.getCompiledEndpointFor(ApiEndpoint.searchArtists),
                {params: searchParams}
            );
    }

    public async getTDLToken(): Promise<AuthTokenRequest> {
        return (await this.http.get<AuthTokenRequest>(
            this.getCompiledEndpointFor(ApiEndpoint.tdlTokenCheck),
            { headers: { X_POOL_ID: this.configuration.clientId }})
        ).data;
    }

    public async logout() {
        const url: string = this.getCompiledEndpointFor(ApiEndpoint.userLogout);

        return this.http.get(
            url,
            { headers: { X_POOL_ID: this.configuration.clientId }}
        );
    }

    public async loadNews<T>(url): Promise<AxiosResponse<{data: T}>> {
        return this.http.get(
            url,
            { headers: { X_POOL_ID: this.configuration.clientId, 'X-Requested-With': 'XMLHttpRequest', accept: 'application/json' }}
        );
    }
}
